import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

export type FormValueProposal = {
  resultId: string
  mappingId: string
  fieldId: string
  fieldLabel: string
  currentValue: unknown
  proposedValue: unknown
  source: string
  status: 'new' | 'changed' | 'unchanged'
}

export type ProposalsResponse = {
  proposals: FormValueProposal[]
  reason?: string
  /** For reason='no-match': instrument types the mappings want vs. what's stored. */
  want?: string
  have?: string
  /** Mappings left out because the row, column or field they fill is gone from the form. */
  skipped?: SkippedMapping[]
}

export type SkippedMapping = {
  mappingId: string
  targetFieldId: string
  label: string
  reason: 'unknown-row' | 'unknown-column' | 'unknown-field'
}

export type InstrumentResultType = 'ella' | 'nc200' | 'epic'
export type MappingSourceKind = 'instrument' | 'patient'

/** A mapping: a source value -> a form field. `instrument` reads an instrument
 * result value (resultType + selector + valueField); `patient` reads a demographic
 * key (valueField) from a patient.new event. */
export type FormFieldMapping = {
  id: string
  sourceKind: MappingSourceKind
  resultType: InstrumentResultType | null
  valueField: string
  selector: Record<string, unknown> | null
  formDefinitionId: string
  targetFieldId: string
  label: string | null
}

/** Per instrument result type, plus a `patient` catalog of demographic keys. */
export type ValueFieldCatalog = Record<InstrumentResultType | 'patient', string[]>

/** An instrument source (integration) a mapping can read from, resolved per tenant. */
export type InstrumentSource = {
  id: string
  resultType: InstrumentResultType
  name: string
  status: string
}

export type CreateMappingPayload = {
  formDefinitionId: string
  targetFieldId: string
  sourceKind?: MappingSourceKind
  resultType?: InstrumentResultType
  valueField: string
  selector?: Record<string, unknown>
  label?: string
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`API ${init?.method ?? 'GET'} ${path} failed (${res.status}): ${text}`)
  }
  return res.json() as Promise<T>
}

export const formPopulationApi = {
  proposals: (submissionId: string) =>
    request<ProposalsResponse>(`/form-population/proposals?submissionId=${encodeURIComponent(submissionId)}`),

  apply: (submissionId: string, accepted: Array<{ resultId: string; mappingId: string }>) =>
    request<{ applied: number; submission: unknown }>(`/form-population/apply`, {
      method: 'POST',
      body: JSON.stringify({ submissionId, accepted }),
    }),

  /** Link a submission's procedure to the demo instrument sample (no real DIN/product yet). */
  correlate: (submissionId: string) =>
    request<{ correlated: boolean }>(`/form-population/correlate`, {
      method: 'POST',
      body: JSON.stringify({ submissionId }),
    }),

  // ── Mapping authoring ──
  getValueFields: () => request<ValueFieldCatalog>(`/form-population/value-fields`),

  /** Instrument sources for the mapping UI, scoped to the selected tenant. */
  getSources: (tenantId: string) =>
    request<InstrumentSource[]>(`/form-population/sources`, {
      headers: { 'X-Tenant-Id': tenantId },
    }),

  listMappings: (formDefinitionId: string) =>
    request<FormFieldMapping[]>(
      `/form-population/mappings?formDefinitionId=${encodeURIComponent(formDefinitionId)}`,
    ),

  createMapping: (payload: CreateMappingPayload) =>
    request<FormFieldMapping>(`/form-population/mappings`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  deleteMapping: (id: string) =>
    request<{ id: string; deleted: boolean }>(`/form-population/mappings/${id}`, {
      method: 'DELETE',
    }),
}
